import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { socialAPI, galleryAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import AppLayout from '../components/AppLayout';
import {
  Link2, ExternalLink, RefreshCw, Instagram, Facebook, Youtube, Twitter, Linkedin,
  CheckCircle2, Loader2, Send, Clock, ChevronDown, Sparkles, Hash, RotateCw, Music
} from 'lucide-react';

// TikTok icon (not in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const PLATFORM_META = {
  instagram: {
    name: 'Instagram',
    icon: Instagram,
    color: 'bg-gradient-to-br from-purple-500 to-pink-500',
    text: 'text-pink-400',
    border: 'border-pink-500/30',
    description: 'Reels',
  },
  facebook: {
    name: 'Facebook',
    icon: Facebook,
    color: 'bg-blue-600',
    text: 'text-blue-400',
    border: 'border-blue-500/30',
    description: 'Reels',
  },
  tiktok: {
    name: 'TikTok',
    icon: TikTokIcon,
    color: 'bg-black',
    text: 'text-[#23211a]',
    border: 'border-[#b5ad99]/30',
    description: 'Video',
  },
  youtube: {
    name: 'YouTube',
    icon: Youtube,
    color: 'bg-[#a34430]',
    text: 'text-[#b3543f]',
    border: 'border-[#b3543f]/40',
    description: 'Shorts / Video',
  },
  x: {
    name: 'X',
    icon: Twitter,
    color: 'bg-black',
    text: 'text-[#4a463b]',
    border: 'border-[#b5ad99]/30',
    description: 'Post',
  },
  linkedin: {
    name: 'LinkedIn',
    icon: Linkedin,
    color: 'bg-sky-700',
    text: 'text-sky-400',
    border: 'border-sky-500/30',
    description: 'Video post',
  },
};

export default function SocialPage() {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [videos, setVideos] = useState([]);
  const [publishHistory, setPublishHistory] = useState([]);
  const [publishingVideoId, setPublishingVideoId] = useState(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState({});
  const [fbPages, setFbPages] = useState([]);
  const [selectedFbPage, setSelectedFbPage] = useState('');
  const [liPages, setLiPages] = useState([]);
  const [selectedLiPage, setSelectedLiPage] = useState('');

  // Publish preview dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [previewCaption, setPreviewCaption] = useState('');
  const [previewHashtags, setPreviewHashtags] = useState('');
  const [generatingCaption, setGeneratingCaption] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [accRes, vidRes, histRes] = await Promise.all([
        socialAPI.getAccounts(),
        galleryAPI.getAllVideos(),
        socialAPI.getHistory(),
      ]);
      setAccounts(accRes.data.accounts || []);
      setVideos(vidRes.data || []);
      setPublishHistory(histRes.data || []);

      const connectedPlatformsList = (accRes.data.accounts || []).map(a => a.platform);

      // Load FB pages if facebook is connected
      if (connectedPlatformsList.includes('facebook')) {
        try {
          const pgRes = await socialAPI.getFacebookPages();
          setFbPages(pgRes.data.pages || []);
          if (pgRes.data.pages?.length === 1) {
            setSelectedFbPage(pgRes.data.pages[0].page_id);
          }
        } catch (e) {
          if (e.response?.status !== 401) toast.error('Could not load Facebook pages');
        }
      }

      // Load LinkedIn pages if linkedin is connected
      if (connectedPlatformsList.includes('linkedin')) {
        try {
          const liRes = await socialAPI.getLinkedInPages();
          setLiPages(liRes.data.pages || []);
          if (liRes.data.pages?.length === 1) {
            setSelectedLiPage(liRes.data.pages[0].page_id || liRes.data.pages[0].id);
          }
        } catch (e) {
          if (e.response?.status !== 401) toast.error('Could not load LinkedIn pages');
        }
      }
    } catch (err) {
      if (err.response?.status !== 401) toast.error('Could not load social media data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Listen for popup close → refresh accounts
  useEffect(() => {
    const handler = (e) => {
      if (e.data === 'social-connected') loadData();
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [loadData]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await socialAPI.connect();
      const { access_url } = res.data;
      // Open Upload Post connect page in a popup
      const popup = window.open(access_url, 'connect-social', 'width=600,height=700,left=200,top=100');
      // Poll for popup close
      const timer = setInterval(() => {
        if (popup?.closed) {
          clearInterval(timer);
          setConnecting(false);
          loadData();
        }
      }, 1000);
    } catch (err) {
      const detail = err.response?.data?.detail || t('social.connectionFailed');
      toast.error(detail);
      setConnecting(false);
    }
  };

  const togglePlatform = (videoId, platform) => {
    setSelectedPlatforms(prev => {
      const current = prev[videoId] || [];
      return {
        ...prev,
        [videoId]: current.includes(platform)
          ? current.filter(p => p !== platform)
          : [...current, platform],
      };
    });
  };

  const openPublishPreview = async (video) => {
    const platforms = selectedPlatforms[video.id] || [];
    if (platforms.length === 0) {
      toast.error('Select at least one platform');
      return;
    }
    if (!video.video_url) {
      toast.error('Video has no URL. Wait for rendering to finish');
      return;
    }
    if (platforms.includes('facebook') && fbPages.length > 1 && !selectedFbPage) {
      toast.error('Select a Facebook page first');
      return;
    }
    if (platforms.includes('linkedin') && liPages.length > 1 && !selectedLiPage) {
      toast.error('Select a LinkedIn page first');
      return;
    }

    setPreviewVideo(video);
    setPreviewCaption('');
    setPreviewHashtags('');
    setPreviewOpen(true);

    // Generate AI caption from property context
    if (video.property_id) {
      setGeneratingCaption(true);
      try {
        const res = await socialAPI.generateCaption(video.property_id);
        setPreviewCaption(res.data.caption || '');
        setPreviewHashtags((res.data.hashtags || []).join(' '));
      } catch (err) {
        // Non-critical: fall back to video title so the user can still post.
        // Surface a soft warning so they know why the caption isn't AI-generated.
        console.error('Failed to generate caption', err);
        if (err?.response?.status !== 401) {
          toast.warning(
            "Caption konnte nicht generiert werden. Video-Titel wird verwendet."
          );
        }
        setPreviewCaption(video.title || video.property?.title || '');
      } finally {
        setGeneratingCaption(false);
      }
    } else {
      setPreviewCaption(video.title || '');
    }
  };

  const regenerateCaption = async () => {
    if (!previewVideo?.property_id) return;
    setGeneratingCaption(true);
    try {
      const res = await socialAPI.generateCaption(previewVideo.property_id);
      setPreviewCaption(res.data.caption || '');
      setPreviewHashtags((res.data.hashtags || []).join(' '));
    } catch (err) {
      toast.error('Failed to regenerate caption');
    } finally {
      setGeneratingCaption(false);
    }
  };

  const handleConfirmPublish = async () => {
    if (!previewVideo) return;
    const platforms = selectedPlatforms[previewVideo.id] || [];

    // Combine caption and hashtags into description
    const description = [previewCaption.trim(), previewHashtags.trim()].filter(Boolean).join('\n\n');

    setPublishing(true);
    setPublishingVideoId(previewVideo.id);
    try {
      const payload = {
        video_url: previewVideo.video_url,
        title: previewVideo.title || previewVideo.property?.title || 'Property video',
        description: description || previewVideo.title || 'Property video',
        platforms,
      };
      // Facebook page
      if (platforms.includes('facebook') && selectedFbPage) {
        payload.facebook_page_id = selectedFbPage;
      }
      // LinkedIn page
      if (platforms.includes('linkedin') && selectedLiPage) {
        payload.linkedin_page_id = selectedLiPage;
      }
      // YouTube defaults
      if (platforms.includes('youtube')) {
        payload.youtube_privacy = 'public';
        // Extract hashtags as tags for YouTube
        const tags = previewHashtags.trim().split(/\s+/).filter(t => t.startsWith('#')).map(t => t.replace('#', ''));
        if (tags.length > 0) {
          payload.youtube_tags = tags;
        }
      }

      const res = await socialAPI.publish(payload);
      const results = res.data?.results;
      let hasError = false;
      if (results) {
        for (const [plat, r] of Object.entries(results)) {
          if (r?.success === false) {
            toast.error(`${plat}: ${r.error || 'Failed'}`);
            hasError = true;
          }
        }
      }
      if (!hasError) {
        const platformNames = platforms.map(p => PLATFORM_META[p]?.name || p).join(' & ');
        toast.success(`Published to ${platformNames}!`);
      }
      setPreviewOpen(false);
      loadData();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to publish';
      toast.error(msg, { duration: 8000 });
    } finally {
      setPublishing(false);
      setPublishingVideoId(null);
    }
  };

  const connectedPlatforms = accounts.map(a => a.platform);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#1f4d3a] animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div data-testid="social-page" className="space-y-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#23211a]">Social Media</h1>
          <p className="text-sm text-[#8a8474] mt-0.5">Connect your accounts and publish videos across all platforms</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadData}
          className="border-[#d8d0bd] text-[#6f6a5c] text-xs"
          data-testid="refresh-accounts-btn"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* Connected Accounts */}
      <Card className="glass border-[#e3dccb]">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-[#23211a] flex items-center gap-2">
            <Link2 className="w-4 h-4 text-[#1f4d3a]" />
            Connected Accounts
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {accounts.length === 0 ? (
            <p className="text-sm text-[#8a8474]">No accounts connected yet. Click below to link your social media accounts.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {accounts.map(acc => {
                const meta = PLATFORM_META[acc.platform] || {};
                const Icon = meta.icon || Link2;
                return (
                  <div
                    key={acc.platform}
                    data-testid={`connected-${acc.platform}`}
                    className={`flex items-center gap-3 p-3 rounded-lg bg-[#fffdf7] border ${meta.border || 'border-[#e3dccb]'}`}
                  >
                    <div className={`p-2 rounded-lg ${meta.color || 'bg-[#d8d0bd]'}`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#23211a] truncate">{acc.display_name || acc.username || meta.name}</p>
                      <p className="text-[10px] text-[#8a8474]">{meta.name} {meta.description ? `· ${meta.description}` : ''}</p>
                    </div>
                    <CheckCircle2 className="w-4 h-4 text-[#1e7a4f] shrink-0" />
                  </div>
                );
              })}
            </div>
          )}

          <Button
            data-testid="connect-accounts-btn"
            onClick={handleConnect}
            disabled={connecting}
            className="w-full bg-[#1f4d3a] hover:bg-[#163a2c] mt-2"
          >
            {connecting ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Connecting...</>
            ) : (
              <><ExternalLink className="w-4 h-4 mr-1.5" /> {accounts.length > 0 ? 'Manage Connections' : 'Connect Social Accounts'}</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Facebook Page Selector (if multiple pages) */}
      {fbPages.length > 1 && (
        <Card className="glass border-[#e3dccb]">
          <CardContent className="pt-5">
            <label className="text-xs font-medium text-[#6f6a5c] mb-1.5 block">Facebook Page</label>
            <div className="relative">
              <select
                data-testid="fb-page-select"
                value={selectedFbPage}
                onChange={(e) => setSelectedFbPage(e.target.value)}
                className="w-full h-10 rounded-lg bg-[#fffdf7] border border-[#d8d0bd] text-[#23211a] px-3 text-sm appearance-none"
              >
                <option value="">Select a page…</option>
                {fbPages.map(p => (
                  <option key={p.page_id} value={p.page_id}>{p.page_name}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-[#8a8474] absolute right-3 top-3 pointer-events-none" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* LinkedIn Page Selector (if multiple pages) */}
      {liPages.length > 1 && (
        <Card className="glass border-[#e3dccb]">
          <CardContent className="pt-5">
            <label className="text-xs font-medium text-[#6f6a5c] mb-1.5 block">LinkedIn Page</label>
            <div className="relative">
              <select
                data-testid="li-page-select"
                value={selectedLiPage}
                onChange={(e) => setSelectedLiPage(e.target.value)}
                className="w-full h-10 rounded-lg bg-[#fffdf7] border border-[#d8d0bd] text-[#23211a] px-3 text-sm appearance-none"
              >
                <option value="">Select a page…</option>
                {liPages.map(p => (
                  <option key={p.page_id || p.id} value={p.page_id || p.id}>{p.page_name || p.name}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-[#8a8474] absolute right-3 top-3 pointer-events-none" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Publish Videos */}
      {accounts.length > 0 && (
        <Card className="glass border-[#e3dccb]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-[#23211a] flex items-center gap-2">
              <Send className="w-4 h-4 text-[#8f6f3a]" />
              Publish Videos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {videos.filter(v => v.video_url && v.status === 'succeeded').length === 0 ? (
              <p className="text-sm text-[#8a8474]">No finished videos yet. Generate some from your properties first.</p>
            ) : (
              <div className="space-y-3">
                {videos.filter(v => v.video_url && v.status === 'succeeded').map(video => {
                  const selected = selectedPlatforms[video.id] || [];
                  const isPublishing = publishingVideoId === video.id;
                  return (
                    <div
                      key={video.id}
                      data-testid={`publish-video-${video.id}`}
                      className="flex items-center gap-4 p-3 rounded-lg bg-[#f2ecdf] border border-[#e3dccb]"
                    >
                      {/* Thumbnail */}
                      <div className="w-20 h-14 rounded-md overflow-hidden bg-[#e9e2d2] shrink-0">
                        {video.thumbnail_url ? (
                          <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[10px] text-[#9c9585]">Video</div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#23211a] truncate">{video.title || video.template_id?.slice(0, 12)}</p>
                        <p className="text-[10px] text-[#8a8474]">{video.created_at ? new Date(video.created_at).toLocaleDateString() : ''}</p>
                      </div>

                      {/* Platform toggles */}
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {connectedPlatforms.map(platform => {
                          const meta = PLATFORM_META[platform];
                          if (!meta) return null;
                          const Icon = meta.icon;
                          const isSelected = selected.includes(platform);
                          return (
                            <button
                              key={platform}
                              data-testid={`toggle-${platform}-${video.id}`}
                              onClick={() => togglePlatform(video.id, platform)}
                              title={meta.name}
                              className={`p-2 rounded-lg border transition-all ${
                                isSelected
                                  ? `${meta.border} bg-[#e9e2d2] ${meta.text}`
                                  : 'border-[#e3dccb] text-[#9c9585] hover:border-[#1f4d3a]/40'
                              }`}
                            >
                              <Icon className="w-4 h-4" />
                            </button>
                          );
                        })}
                      </div>

                      {/* Publish button */}
                      <Button
                        size="sm"
                        data-testid={`publish-btn-${video.id}`}
                        onClick={() => openPublishPreview(video)}
                        disabled={isPublishing || selected.length === 0}
                        className="bg-[#8f6f3a] hover:bg-[#75592e] text-xs h-8 px-3"
                      >
                        {isPublishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Publish'}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Publish History */}
      {publishHistory.length > 0 && (
        <Card className="glass border-[#e3dccb]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-[#23211a] flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#9a6d1f]" />
              Publish History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {publishHistory.slice(0, 20).map(record => (
                <div key={record.id} className="flex items-center justify-between p-2.5 rounded-lg bg-[#f2ecdf] border border-[#e3dccb]">
                  <div className="flex items-center gap-2 min-w-0">
                    {record.platforms?.map(p => {
                      const meta = PLATFORM_META[p];
                      const Icon = meta?.icon || Link2;
                      return <Icon key={p} className={`w-3.5 h-3.5 shrink-0 ${meta?.text || 'text-[#6f6a5c]'}`} />;
                    })}
                    <span className="text-xs text-[#6f6a5c] truncate">{record.title}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      record.status === 'failed' ? 'bg-[#a34430]/20 text-[#a34430]' :
                      record.status === 'submitted' ? 'bg-[#f0e2c2] text-[#9a6d1f]' : 'bg-[#d6e8dd] text-[#1e7a4f]'
                    }`}>
                      {record.status}
                    </span>
                    {record.error && (
                      <span className="text-[10px] text-[#b3543f] max-w-[180px] truncate" title={record.error}>{record.error}</span>
                    )}
                    <span className="text-[10px] text-[#9c9585]">
                      {record.created_at ? new Date(record.created_at).toLocaleDateString() : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Publish Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-[#fffdf7] border-[#d8d0bd] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#23211a] flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#1f4d3a]" />
              Publish Preview
            </DialogTitle>
            <DialogDescription className="text-[#6f6a5c]">
              Review and edit the caption before publishing
            </DialogDescription>
          </DialogHeader>

          {/* Target platforms pills */}
          {previewVideo && (
            <div className="flex flex-wrap gap-1.5">
              {(selectedPlatforms[previewVideo.id] || []).map(p => {
                const meta = PLATFORM_META[p];
                if (!meta) return null;
                const Icon = meta.icon;
                return (
                  <span key={p} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium border ${meta.border} ${meta.text} bg-[#efe9db]`}>
                    <Icon className="w-3 h-3" />
                    {meta.name}
                  </span>
                );
              })}
            </div>
          )}

          {/* Video info */}
          {previewVideo && (
            <div className="flex items-center gap-3 p-2.5 rounded-lg bg-[#efe9db] border border-[#d8d0bd]">
              {previewVideo.thumbnail_url ? (
                <img src={previewVideo.thumbnail_url} alt="" className="w-16 h-11 rounded object-cover shrink-0" />
              ) : (
                <div className="w-16 h-11 rounded bg-[#d8d0bd] flex items-center justify-center text-[10px] text-[#8a8474] shrink-0">Video</div>
              )}
              <div className="min-w-0">
                <p className="text-sm text-[#23211a] truncate">{previewVideo.title || previewVideo.property?.title}</p>
                <p className="text-[10px] text-[#8a8474]">{previewVideo.property?.location}</p>
              </div>
            </div>
          )}

          {/* Platform notes */}
          {previewVideo && (selectedPlatforms[previewVideo.id] || []).includes('x') && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-[#f2ecdf] border border-[#d8d0bd]">
              <Twitter className="w-3.5 h-3.5 text-[#6f6a5c] shrink-0" />
              <p className="text-[10px] text-[#8a8474]">X posts are limited to 280 characters. Longer captions will be auto-trimmed.</p>
            </div>
          )}

          {/* Caption */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-[#6f6a5c]">Caption</label>
              <Button
                variant="ghost"
                size="sm"
                onClick={regenerateCaption}
                disabled={generatingCaption || !previewVideo?.property_id}
                className="text-[#1f4d3a] hover:text-[#1f4d3a] h-6 px-2 text-[10px]"
              >
                {generatingCaption ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RotateCw className="w-3 h-3 mr-1" />}
                Regenerate
              </Button>
            </div>
            {generatingCaption ? (
              <div className="flex items-center justify-center h-24 rounded-lg bg-[#e9e2d2] border border-[#d8d0bd]">
                <Loader2 className="w-5 h-5 text-[#1f4d3a] animate-spin mr-2" />
                <span className="text-xs text-[#6f6a5c]">Generating caption...</span>
              </div>
            ) : (
              <textarea
                value={previewCaption}
                onChange={(e) => setPreviewCaption(e.target.value)}
                rows={5}
                className="w-full rounded-lg bg-[#e9e2d2] border border-[#d8d0bd] text-[#23211a] px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#1f4d3a] focus:border-[#1f4d3a]"
                placeholder="Write your caption..."
              />
            )}
          </div>

          {/* Hashtags */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#6f6a5c] flex items-center gap-1">
              <Hash className="w-3 h-3" /> Hashtags
            </label>
            {generatingCaption ? (
              <div className="h-10 rounded-lg bg-[#e9e2d2] border border-[#d8d0bd] animate-pulse" />
            ) : (
              <textarea
                value={previewHashtags}
                onChange={(e) => setPreviewHashtags(e.target.value)}
                rows={2}
                className="w-full rounded-lg bg-[#e9e2d2] border border-[#d8d0bd] text-[#23211a] px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#1f4d3a] focus:border-[#1f4d3a]"
                placeholder="#realestate #property #home"
              />
            )}
          </div>

          {/* Preview of combined text */}
          {(previewCaption || previewHashtags) && !generatingCaption && (
            <div className="space-y-1.5">
              <label className="text-[10px] font-medium text-[#8a8474] uppercase tracking-wider">Full post text</label>
              <div className="p-3 rounded-lg bg-[#f7f3ea] border border-[#e3dccb] text-xs text-[#6f6a5c] whitespace-pre-wrap max-h-32 overflow-y-auto">
                {[previewCaption.trim(), previewHashtags.trim()].filter(Boolean).join('\n\n')}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setPreviewOpen(false)}
              className="border-[#d8d0bd] text-[#4a463b]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmPublish}
              disabled={publishing || generatingCaption}
              className="bg-[#8f6f3a] hover:bg-[#75592e]"
            >
              {publishing ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Publishing...</>
              ) : (
                <><Send className="w-4 h-4 mr-1.5" /> Publish Now</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </AppLayout>
  );
}
